import type { EntryValueT } from "@/types/database/entries";
import { create } from "zustand";
import { createJSONStorage, devtools, persist } from "zustand/middleware";

import { onlyThisEntryValues } from "../../pages/utils/onlyThisEntryValues";
import { updateEntryValues } from "./updateEntryValues";

export type UpsertEntryValueT = Partial<EntryValueT> & {
    entry_id: string;
    input_id: string;
    date_value?: Date | null;
    json_value?: Record<string, unknown> | null;
};

type State = {
    entry_values: EntryValueT[];
};

type Actions = {
    upsertEntryValue: (entryValue: UpsertEntryValueT) => void;
    upsertEntryValues: (entryValues: UpsertEntryValueT[]) => void;
    removeEntryValuesByInputIds: (inputIds: readonly string[]) => void;
    /** Drops answers that belong to any other entry. */
    keepOnlyEntry: (entryId: string) => void;
    getEntryValues: () => EntryValueT[];
    resetEntryValues: () => void;
    getEntryValueByInputId: (input_id: string) => EntryValueT | null;
};

const initialState: State = {
    entry_values: [],
};

export const useEntryValuesStore = create<State & Actions>()(
    devtools(
        persist(
            (set, get) => ({
                ...initialState,
                getEntryValues: () => {
                    return get().entry_values;
                },
                resetEntryValues: () => {
                    set({ ...get(), entry_values: [] });
                },
                getEntryValueByInputId: (input_id: string) => {
                    const entryValues = get().entry_values;
                    return entryValues.find((ev) => ev.input_id === input_id) ?? null;
                },
                upsertEntryValue: (newVal: UpsertEntryValueT) => {
                    set((state) => {
                        const updatedEntryValues = updateEntryValues(state.entry_values, newVal);
                        return { entry_values: updatedEntryValues };
                    });
                },
                removeEntryValuesByInputIds: (inputIds: readonly string[]) => {
                    if (inputIds.length === 0) return;
                    const removed = new Set(inputIds);
                    set((state) => ({
                        entry_values: state.entry_values.filter((ev) => !removed.has(ev.input_id)),
                    }));
                },
                keepOnlyEntry: (entryId: string) => {
                    set((state) => {
                        const kept = onlyThisEntryValues(state.entry_values, entryId);
                        return kept.length === state.entry_values.length ? state : { entry_values: kept };
                    });
                },
                upsertEntryValues: (newVals: UpsertEntryValueT[]) => {
                    set((state) => {
                        let updatedEntryValues = [...state.entry_values];
                        for (const newVal of newVals) {
                            updatedEntryValues = updateEntryValues(updatedEntryValues, newVal);
                        }
                        return { entry_values: updatedEntryValues };
                    });
                },
            }),
            {
                name: "entry-values-store",
                storage: createJSONStorage(() => localStorage),
            }
        )
    )
);
